import { AppTitle } from '@/components/atoms/AppTitle/AppTitle'
import { saveUser } from '@/features/Auth/auth.hooks'
import { AppRoutes } from '@/routes'
import { totpService } from '@/services/totp.service'
import { trackUserAction } from '@/shared/helpers/sentry'
import { useAuthStore } from '@/store'
import { useOtpStore } from '@/store/otp/otp.store'
import { Button, Flex, message } from 'antd'
import { AxiosError } from 'axios'
import * as Sentry from '@sentry/nextjs'
import { useRouter } from 'nextjs-toploader/app'
import React, { useCallback, useState } from 'react'
import { OtpInput } from 'reactjs-otp-input'
import styles from './AuthTotpForm.module.scss'
import { AuthStep } from '@/features/Auth/auth.types'

type Props = {
  onBack?: () => void;
  setStep: (step: AuthStep) => void;
}

export function AuthTotpForm({ setStep, onBack }: Props) {
  const [isLoading, setIsLoading] = useState(false)
  const [otpCode, setOtpCode] = useState('')
  const [btnLocked, setBtnLocked] = useState<boolean>(true)
  const userId = useAuthStore((state) => state.userId)
  const router = useRouter()
  const type = useOtpStore((state) => state.type)

  const handlePinValue = useCallback((pin: string) => {
    if (pin.split('').length === 6) {
      setBtnLocked(false)
      setOtpCode(pin)
    } else {
      setBtnLocked(true)
    }
  }, [])

  const handleConfirm = useCallback(async () => {
    setIsLoading(true)
    trackUserAction('User clicked Confirm on TOTP step')
    try {
      const res = await totpService.verify2fa(userId, otpCode)
      if ('accessToken' in res) {
        saveUser({ accessToken: res.accessToken, refreshToken: res.refreshToken })
        trackUserAction('TOTP verification succeeded')
        if (type === 'directEmailLogin') {
          router.replace(`${AppRoutes.INDEX_PAGE}`)
        } else {
          setStep(AuthStep.WALLET_CONFIRMED)
          router.replace(`${AppRoutes.AUTH_SIGNIN}?step=${AuthStep.WALLET_CONFIRMED}`)
        }
      }
    } catch (e) {
      Sentry.captureException(e)
      if (e instanceof AxiosError) {
        message.error(e?.response?.data?.message)
      }
    }
    setIsLoading(false)
  }, [otpCode, router, setStep, type, userId])

  return (
    <Flex vertical justify="center" align="center" className="mobileContainer">
      <Flex className="w-full" flex={1} gap={15} justify="center" vertical>
        <span className={styles.icon}>📌</span>
        <AppTitle text="Enter your 6-digit PIN" centered />
        <p className={styles.text}>
          To complete the login process, please enter the 6-digit PIN code sent to your registered device for two-factor authentication (2FA).
        </p>
        <OtpInput
          containerStyle="otp-container"
          className="otp-input otp-six"
          inputStyle={{ width: '55px' }}
          isInputNum
          numInputs={6}
          onChange={handlePinValue}
        />
      </Flex>
      <Flex className={styles.button} vertical gap={10}>
        <Button
          loading={isLoading}
          disabled={btnLocked}
          className="mobilePrimaryButton"
          onClick={handleConfirm}
          type="primary"
        >
          Confirm
        </Button>
        {onBack && (
          <Button onClick={onBack} className="mobileSecondaryButton">Back</Button>
        )}
      </Flex>
    </Flex>
  )
}
